import { UserToken } from "@app/store/useUserVarStore";
import fetchBHApi from "@helper/fetchBHApi";
import { shouldShowReactions } from "@helper/shouldShowReactions";
import {
  CD_LANGUAGE,
  useBHCustomEventAnalyticsCall,
} from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import {
  EmptyReactionsResponse,
  HandleReactionClick,
  ReactionProp,
  ReactionsDataRequest,
} from "@typings/Reactions.d";
import { useCallback, useEffect, useState } from "react";

type UseRestaurantReactionsProps = {
  currentReactions: ReactionProp[];
  articleId: string;
  userToken: UserToken;
  publishDate: string;
  reactionSetId?: string;
  uniqueSectionName?: string;
};

export function useRestaurantReactions({
  currentReactions,
  articleId,
  userToken,
  publishDate,
  uniqueSectionName,
  reactionSetId,
}: UseRestaurantReactionsProps) {
  const [reactions, setReactions] = useState<ReactionProp[]>(currentReactions);
  const [userReactions, setUserReactions] = useState<string>("0");

  const showReactions = shouldShowReactions(publishDate, uniqueSectionName);
  const bhCustomEvent = useBHCustomEventAnalyticsCall();

  useEffect(() => {
    // Sync when parent updates reactions
    if (currentReactions.length && reactions.length === 0) {
      setReactions(currentReactions);
    }
  }, [currentReactions, reactions]);

  // Handle reaction click
  const handleReactionClick: HandleReactionClick = useCallback(
    async (reactId, variant) => {
      const prevReactId = userReactions;

      if (prevReactId !== "0" && reactId === prevReactId) {
        reactId = "0";
      }

      setReactions((prevReactions) =>
        prevReactions.map((reaction) => {
          if (reaction.id === reactId) {
            return { ...reaction, count: reaction.count + 1 };
          } else if (reaction.id === prevReactId) {
            return { ...reaction, count: reaction.count - 1 };
          }
          return reaction;
        }),
      );
      setUserReactions(reactId);

      try {
        if (!showReactions) {
          throw new Error("Reaction is disabled");
        }

        const url = `reactions/${reactId !== "0" ? "user-reacts" : "delete-user-reacts"}`;
        const requestData: ReactionsDataRequest = {
          articleId,
          userToken,
          reactId,
          setId: reactionSetId || "set1",
        };

        const results = await fetchBHApi<EmptyReactionsResponse>(
          url,
          "POST",
          requestData,
        );

        if (results.message === "Success") {
          bhCustomEvent(
            "reaction",
            `${variant}::${reactId}`,
            window.location.href,
            true,
            undefined,
            CD_LANGUAGE.MALAY,
            undefined,
          );
        } else {
          // rollback
          setReactions((prevReactions) =>
            prevReactions.map((reaction) => {
              if (reaction.id === reactId) {
                return { ...reaction, count: reaction.count - 1 };
              } else if (reaction.id === prevReactId) {
                return { ...reaction, count: reaction.count + 1 };
              }
              return reaction;
            }),
          );
        }
      } catch (err) {
        console.error("Error posting reaction:", err);
      }
    },
    [
      articleId,
      userToken,
      reactionSetId,
      userReactions,
      showReactions,
      bhCustomEvent,
    ],
  );

  return {
    reactions,
    userReactions,
    handleReactionClick,
    showReactions,
  };
}
